import { User } from 'lucide-react'
import { useAuthedImageUrl } from '@/hooks/use-authed-image-url'
import { cn } from '@/utils/cn'

export interface FaceTileProps {
  /** Auth-gated `/api/v1/files/...` path of the crop, or null when there is none. */
  url: string | null | undefined
  /** Alt text, already translated by the caller. */
  alt: string
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const SIZE: Record<NonNullable<FaceTileProps['size']>, string> = {
  sm: 'size-10',
  md: 'size-14',
  lg: 'size-20',
}

/**
 * One face crop. The bytes sit behind the auth-gated files endpoint, which a
 * bare `<img src>` cannot reach, so the URL goes through `useAuthedImageUrl`.
 * The person icon stays mounted underneath: it covers the fetch and the error.
 */
export function FaceTile({ url, alt, size = 'md', className }: FaceTileProps) {
  const blobUrl = useAuthedImageUrl(url)
  return (
    <div
      className={cn(
        'relative shrink-0 overflow-hidden rounded-md border border-border bg-muted',
        SIZE[size],
        className
      )}
    >
      {blobUrl ? (
        <img src={blobUrl} alt={alt} className="relative z-10 size-full object-cover" />
      ) : null}
      <div
        className="absolute inset-0 flex items-center justify-center"
        aria-hidden={blobUrl !== null}
      >
        <User className="size-1/2 text-muted-foreground" />
      </div>
    </div>
  )
}
